import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAppState, Task } from '../../contexts/AppContext';

interface AddTaskModalProps {
    isOpen: boolean;
    onClose: () => void;
    editTask?: Task | null;
}

export default function AddTaskModal({ isOpen, onClose, editTask }: AddTaskModalProps) {
    const { addTask, updateTask, projects } = useAppState();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState<Task['priority']>('normal');
    const [dueDate, setDueDate] = useState('');
    const [timeStart, setTimeStart] = useState('');
    const [timeEnd, setTimeEnd] = useState('');
    const [projectId, setProjectId] = useState<string>('');
    const [reminderMinutes, setReminderMinutes] = useState<number | undefined>();

    // Convert 24hr time input ("14:00") to 12hr format ("2:00 PM")
    const to12HourFormat = (time24: string): string => {
        if (!time24) return '';
        const [hours, minutes] = time24.split(':');
        const h = parseInt(hours);
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${minutes} ${ampm}`;
    };

    // Convert 12hr format ("2:00 PM") to 24hr input format ("14:00")
    const to24HourFormat = (time12: string): string => {
        if (!time12) return '';
        const match = time12.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return '';
        let h = parseInt(match[1]);
        const m = match[2];
        const ampm = match[3].toUpperCase();
        if (ampm === 'PM' && h !== 12) h += 12;
        if (ampm === 'AM' && h === 12) h = 0;
        return `${h.toString().padStart(2, '0')}:${m}`;
    };

    // Load edit data
    useEffect(() => {
        if (editTask) {
            setTitle(editTask.title);
            setDescription(editTask.description || '');
            setPriority(editTask.priority);
            setDueDate(editTask.dueDate || '');
            // Convert 12hr format to 24hr for the input field
            setTimeStart(to24HourFormat(editTask.timeStart || ''));
            setTimeEnd(to24HourFormat(editTask.timeEnd || ''));
            setProjectId(editTask.projectId || '');
            setReminderMinutes(editTask.reminderMinutes);
        } else {
            setTitle('');
            setDescription('');
            setPriority('normal');
            setDueDate('');
            setTimeStart('');
            setTimeEnd('');
            setProjectId('');
            setReminderMinutes(undefined);
        }
    }, [editTask, isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return;

        // Get project colors or defaults
        const project = projects.find((p) => p.id === projectId);
        const contextColor = project?.color || '#7B8C9B';
        const contextBgColor = project?.bgColor || '#F5F8FA';
        const context = project?.name || 'No Project';

        const taskData = {
            title: title.trim(),
            description: description.trim() || undefined,
            completed: editTask?.completed || false,
            priority,
            dueDate: dueDate || undefined,
            timeStart: timeStart ? to12HourFormat(timeStart) : undefined,
            timeEnd: timeEnd ? to12HourFormat(timeEnd) : undefined,
            projectId: projectId || undefined,
            context,
            contextColor,
            contextBgColor,
            subtasks: editTask?.subtasks || [],
            reminderMinutes,
        };

        if (editTask) {
            updateTask(editTask.id, taskData);
        } else {
            addTask(taskData);
        }

        // Reset form
        setTitle('');
        setDescription('');
        setPriority('normal');
        setDueDate('');
        setTimeStart('');
        setTimeEnd('');
        setProjectId('');
        setReminderMinutes(undefined);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
            <div className="bg-white dark:bg-dark-card w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl p-6 pb-8 max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-semibold text-[#1A2832] dark:text-dark-text">
                        {editTask ? 'Edit Task' : 'Add Task'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[#F5F8FA] dark:hover:bg-dark-elevated"
                    >
                        <X className="w-5 h-5 text-[#7B8C9B]" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                            Task Title
                        </label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="What needs to be done?"
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated"
                            autoFocus
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                            Description <span className="text-[#7B8C9B] font-normal">(optional)</span>
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add notes or details..."
                            rows={2}
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated resize-none"
                        />
                    </div>

                    {/* Project */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                            Project
                        </label>
                        <select
                            value={projectId}
                            onChange={(e) => setProjectId(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated"
                        >
                            <option value="">No Project</option>
                            {projects.map((project) => (
                                <option key={project.id} value={project.id}>
                                    {project.icon} {project.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Priority */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                            Priority
                        </label>
                        <div className="flex gap-2">
                            {(['high', 'medium', 'normal'] as const).map((p) => (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setPriority(p)}
                                    className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize transition ${priority === p
                                        ? p === 'high'
                                            ? 'bg-[#D66B5C] text-white'
                                            : p === 'medium'
                                                ? 'bg-[#E8A547] text-white'
                                                : 'bg-[#0D5F6B] text-white'
                                        : 'bg-[#F5F8FA] dark:bg-dark-elevated text-[#4A5C6A] dark:text-dark-text-secondary'
                                        }`}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Date & Time */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                            Due Date
                        </label>
                        <input
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated"
                        />
                    </div>

                    {/* Time */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                                Start Time
                            </label>
                            <input
                                type="time"
                                value={timeStart}
                                onChange={(e) => setTimeStart(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                                End Time
                            </label>
                            <input
                                type="time"
                                value={timeEnd}
                                onChange={(e) => setTimeEnd(e.target.value)}
                                className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated"
                            />
                        </div>
                    </div>

                    {/* Reminder */}
                    <div>
                        <label className="block text-sm font-medium text-[#1A2832] dark:text-dark-text mb-2">
                            Reminder
                        </label>
                        <select
                            value={reminderMinutes ?? ''}
                            onChange={(e) => setReminderMinutes(e.target.value ? Number(e.target.value) : undefined)}
                            className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] dark:border-dark-border focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] dark:text-dark-text bg-white dark:bg-dark-elevated"
                        >
                            <option value="">No reminder</option>
                            <option value="0">At time</option>
                            <option value="5">5 minutes before</option>
                            <option value="15">15 minutes before</option>
                            <option value="30">30 minutes before</option>
                            <option value="60">1 hour before</option>
                            <option value="1440">1 day before</option>
                        </select>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={!title.trim()}
                        className="w-full py-4 rounded-xl bg-[#0D5F6B] text-white text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_16px_rgba(13,95,107,0.08),0_2px_4px_rgba(13,95,107,0.04)]"
                    >
                        {editTask ? 'Save Changes' : 'Add Task'}
                    </button>
                </form>
            </div>
        </div>
    );
}
